import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, EnvelopeSimple, Eye, EyeSlash, LockKey, Spinner, User } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/errors";

export default function LeaderRegister() {
  const { leader, registerLeader } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const nextParam = new URLSearchParams(loc.search).get("next");
  const returnTo = nextParam || "/start";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  if (leader) return <Navigate to={returnTo} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await registerLeader({ email: email.trim(), password, name: name.trim() || null });
      toast.success("Account created.");
      nav(returnTo, { replace: true });
    } catch (err) {
      toast.error(apiError(err, "Could not create account"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 sm:px-8 py-16 lg:py-24">
      <div className="anim-in delay-1">
        <div className="label-kicker" style={{ color: "#1E3A2B" }}>Create your account</div>
        <h1 className="font-serif text-4xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
          So your work is saved.
        </h1>
        <p className="text-[#4A524C] mt-3 leading-relaxed">
          Optional — you can keep using the app anonymously. Sign-up just lets you return to past conversations.
        </p>
      </div>
      <form onSubmit={submit} className="mt-8 space-y-4 anim-in delay-2">
        <label className="block">
          <span className="label-kicker block mb-2">Name (optional)</span>
          <div className="relative">
            <User size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="leader-register-name"
              className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-4 py-3.5 text-[#121815]"
            />
          </div>
        </label>
        <label className="block">
          <span className="label-kicker block mb-2">Email</span>
          <div className="relative">
            <EnvelopeSimple size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
            <input
              type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="leader-register-email"
              className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-4 py-3.5 text-[#121815]"
            />
          </div>
        </label>
        <label className="block">
          <span className="label-kicker block mb-2">Password (8+ characters)</span>
          <div className="relative">
            <LockKey size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
            <input
              type={showPw ? "text" : "password"} required minLength={8} autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="leader-register-password"
              className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-12 py-3.5 text-[#121815]"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              data-testid="leader-register-toggle-pw"
              aria-label={showPw ? "Hide password" : "Show password"}
              tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-md text-[#8A948E] hover:text-[#1E3A2B] hover:bg-[#F3F0EA] transition-colors"
            >
              {showPw ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
            </button>
          </div>
        </label>
        <button
          type="submit" disabled={busy}
          data-testid="leader-register-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#1E3A2B] text-white px-5 py-3.5 rounded-xl font-medium hover:bg-[#2C4C3B] disabled:opacity-60"
        >
          {busy ? <Spinner size={18} className="animate-spin" /> : <CheckCircle size={18} weight="duotone" />}
          Create account
        </button>
        <p className="text-xs text-[#8A948E] text-center pt-2">
          Already have an account? <Link to="/auth/login" className="text-[#1E3A2B] underline">Sign in →</Link>
        </p>
      </form>
    </div>
  );
}
